import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { RouterProvider } from "react-router-dom";
import configureAxios from "./configure-axios.ts";
import store from "./store/store.ts";
import fetchConfig from "./config-json.ts";
import { ConfigJsonContext } from "./providers/config-json-provider.ts";

import "./index.scss";
import createRouter from "./providers/conte-router.tsx";
import initStore from "./store/actions/init-store.action.ts";
import createWebsocket from "./websocket/websocket-factory.ts";
import initCwr from "./cwr.ts";
import { AppRoutes } from "./enums/app-routes.ts";
import Loader from "./layouts/components/loader.tsx";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { initTagManager } from "./features/analytics/analytics.service.ts";
import initCoralogixRum from "./coralogix.ts";
import { error } from "./utilities/logger.ts";
import { envFlag } from "./utilities/env-util.ts";

async function enableMocking() {
  if (!(envFlag("VITE_ENABLE_MOCKS", import.meta.env) ?? false)) return;
  const { worker } = await import("./mocks/browsers.ts");
  return worker.start({
    onUnhandledRequest: "bypass",
  });
}

const root = ReactDOM.createRoot(document.getElementById("root")!);

window.addEventListener("vite:preloadError", (event) => {
  error(event);
  window.location.reload(); // for example, refresh the page
});

root.render(<Loader />);

fetchConfig()
  .then(async (configJson) => {
    configureAxios(configJson);
    initCoralogixRum(configJson);
    initCwr(configJson);
    return enableMocking().then(async () => {
      if (configJson.GTM_ID) {
        initTagManager(configJson.GTM_ID);
      }

      createWebsocket(store, configJson.API_ENDPOINT_WS);
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      if (
        !new RegExp(`^/${AppRoutes.PERSONAL_AREA}/`).test(
          window.location.pathname,
        )
      ) {
        const workflowId = urlParams.get("id") ?? undefined;
        const isExternalChannel = !!urlParams.get("channel");
        await store.dispatch(initStore({ workflowId, isExternalChannel }));
      }

      const queryClient = new QueryClient();
      const router = createRouter(store, configJson, queryClient);

      root.render(
        <ConfigJsonContext.Provider value={configJson}>
          <QueryClientProvider client={queryClient}>
            <Provider store={store}>
              <RouterProvider router={router} />
            </Provider>
          </QueryClientProvider>
        </ConfigJsonContext.Provider>,
      );
    });
  })
  .then(() => {});
